import React, { useEffect, useState } from 'react';
import { getColumnKeys, NavScreenGroup, NavScreenOption } from 'hooks/nav/useAccessibleNavScreens';
import ChevronDown from 'assets/svg/ChevronDown';

interface Props {
    group: NavScreenGroup;
    activeItemId?: string;
    onSelect: (group: NavScreenGroup, url: string, item: NavScreenOption) => void;
    onToggle: (group: NavScreenGroup, expanded: boolean) => void;
}

const containsItem = (group: NavScreenGroup, itemId?: string): boolean =>
    Boolean(itemId) && getColumnKeys(group).some((key) => group.options[key].some((item) => item?.id === itemId));

// Shared by the option rows and by a childless group rendered as a row of its own, so the two
// cannot drift apart visually. The design fixes the row at 32px around a 21px line box, so the
// 5.5px padding is what centres the label in it.
const rowClassName = (isActive: boolean): string =>
    `block h-8 w-full truncate rounded-lg px-3 py-[5.5px] text-left font-xs leading-[21px] ${
        isActive ? 'bg-filterV2PrimaryBg text-filterV2Primary' : 'text-filterV2TextPrimary hover:bg-filterV2PrimaryBg'
    }`;

// One top-level `screens[]` entry: a collapsible header, optional sub-group headers, and the
// routes under them.
function NavPanelGroup({ group, activeItemId, onSelect, onToggle }: Props) {
    const [isExpanded, setIsExpanded] = useState(() => containsItem(group, activeItemId));
    const columnKeys = getColumnKeys(group);

    // Navigation can land in a different group while the panel stays open (rail home, deep
    // link). Expand only — collapsing the group you are already on stays the user's choice.
    useEffect(() => {
        if (containsItem(group, activeItemId)) setIsExpanded(true);
    }, [group, activeItemId]);

    const toggle = () => {
        const next = !isExpanded;
        setIsExpanded(next);
        onToggle(group, next);
    };

    // A group that is a destination itself and has nothing nested under it yet — an admin-created
    // top-level embedded route. Expanding it would reveal an empty panel, so it navigates instead.
    if (!columnKeys.length && group.url) {
        const isActive = Boolean(group.id) && group.id === activeItemId;
        return (
            <div className="border-b border-filterV2Border px-4 pb-5">
                <button
                    type="button"
                    onClick={() => onSelect(group, group.url as string, { label: group.label, path: '', id: group.id })}
                    className={rowClassName(isActive)}
                >
                    {group.label}
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 border-b border-filterV2Border px-4 pb-5">
            <button
                type="button"
                onClick={toggle}
                aria-expanded={isExpanded}
                className="flex w-full items-center justify-between gap-2 pl-2 text-left font-xs font-medium leading-[normal] text-filterV2TextPrimary"
            >
                <span className="break-words">{group.label}</span>
                <span className="flex shrink-0">
                    <ChevronDown direction={isExpanded ? 'up' : 'down'} />
                </span>
            </button>

            {isExpanded &&
                columnKeys.map((key, columnIndex) => {
                    // A sub-type with no routes under it (e.g. "Predictive" when nothing in it is
                    // accessible) has nothing to show — hide the whole block, sub-header included,
                    // rather than render a dangling heading. `columnIndex` still tracks the
                    // original position so the positional `group.path` URL fallback stays correct.
                    if (!group.options[key].length) return null;
                    return (
                        <div key={key} className="flex flex-col gap-2">
                            {group.showSubHeader && (
                                <div className="pl-2 font-xs font-medium leading-[normal] text-navV2TextMuted">
                                    {key}
                                </div>
                            )}
                            <div className="flex flex-col">
                                {group.options[key].map((item) => {
                                    // `item.url` is the route `screenDetailsWithRoutes` registers for
                                    // this screen; the positional `group.path` prefix only covers
                                    // options that register no route of their own.
                                    const url = item.url ?? `/${group.path?.[columnIndex]}/${item.path}`;
                                    const isActive = Boolean(item?.id) && item.id === activeItemId;
                                    return (
                                        <button
                                            type="button"
                                            key={`${key}-${item.path}`}
                                            onClick={() => onSelect(group, url, item)}
                                            className={rowClassName(isActive)}
                                        >
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
        </div>
    );
}

export default NavPanelGroup;
